"use client";

import { USERS_FIELDS } from "@/constants/users";
import { UserContext } from "@/contexts/UserContext";
import { toaster } from "@cloudspire/shared/components/ui/toaster";
import { useApi } from "@cloudspire/shared/hooks/useApi";
import { useApiRouter } from "@cloudspire/shared/hooks/useApiRouter";
import { useAuthState } from "@cloudspire/shared/hooks/useAuth";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";

export function useUser() {
  const value = useContext(UserContext);

  if (value === null) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return value;
}

/**
 * @typedef {object} Props
 * @prop {import("@cloudspire/shared/types/alfred/ApiResponse").ApiResponse<import("@cloudspire/shared/types/alfred/ApiResponse").Attr<import("@cloudspire/shared/types/alfred/UserProfile").UserProfile>>} [initialUserParsedJson]
 * @prop {React.ReactNode} children
 */
/**
 * @param {Props} props
 */
export function UserProvider({ children, initialUserParsedJson }) {
  const intl = useIntl();

  const { authenticated } = useAuthState();

  const { apiRouter } = useApiRouter();

  const url = useMemo(() => {
    return apiRouter.getUrl({
      id: "@api/auth@me",
      overrides: {
        query: USERS_FIELDS,
      },
    });
  }, [apiRouter]);

  const reloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  const handleError = useCallback(
    /**
     * @param {any} error
     */
    (error) => {
      if (
        error?.name === "TypeError" &&
        error.message.includes("NetworkError")
      ) {
        return;
      }

      toaster.error({
        action: {
          label: intl.formatMessage({ defaultMessage: "Recharger" }),
          onClick: reloadPage,
        },
        description: intl.formatMessage({
          defaultMessage: "Veuillez réessayer plus tard.",
        }),
        title: intl.formatMessage({
          defaultMessage:
            "Une erreur est survenue lors de la récupération de votre profil utilisateur.",
        }),
      });
    },
    [intl, reloadPage],
  );

  /** @type {import("@cloudspire/shared/hooks/useApi").UseApi<import("@cloudspire/shared/types/alfred/ApiResponse").Attr<import("@cloudspire/shared/types/alfred/UserProfile").UserProfile>>} */
  const { swrResponse } = useApi(authenticated ? url : null, {
    swrConfig: {
      fallbackData: initialUserParsedJson
        ? { parsedJson: initialUserParsedJson, response: new Response() }
        : undefined,
      keepPreviousData: true,
      onError: handleError,
      refreshInterval: 10000,
      revalidateOnFocus: true,
      shouldRetryOnError: true,
    },
  });
  const { mutate } = swrResponse;

  const value = useMemo(() => {
    /** @type {import("@/contexts/UserContext").UserContextValue} */
    const value = {
      mutateUser: mutate,
      user: swrResponse?.data?.parsedJson?.data?.attributes,
    };
    return value;
  }, [swrResponse.data?.parsedJson?.data, mutate]);

  useEffect(() => {
    /**
     * @param {PageTransitionEvent} event
     */
    const handlePageShow = (event) => {
      // If the page was restored from the bfcache, re-validate the session
      if (event.persisted) {
        mutate();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [mutate]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
